// Season state machine: PREP → PLANT → MAINTAIN → HARVEST → PREP …
// Operates on the active kingdom's state slice — each kingdom runs its own
// independent season cycle.

import { Events, emit, on } from '../ui/eventBus.js'
import { Seasons, SeasonOrder, SeasonConfig, KingdomConfig, GoonTypes, WorkModes, RiceValue, SeasonDurationsMs, TaxConfig } from '../config/balance.js'
import { active, activeId, persist, getProfile } from './state.js'
import { computeClickValue } from './clickValueCalc.js'
import { emptyCombo, comboAt, comboAfterClick, comboMult, comboLive } from './combo.js'
import { ComboConfig } from '../config/balance.js'
import * as glint from './glint.js'
import { citizensWorking } from './restManager.js' // runtime-only (resting citizens earn / add nothing)
import * as profile from './profileManager.js' // runtime-only (PhD click bonus)
import * as citizens from './citizens.js'      // runtime-only (per-citizen roster + school payout)
import * as hospital from './hospital.js'      // runtime-only (patients earn nothing)
import { gravesAfter, gravesTotal } from './hungerRules.js'

// Per-kingdom palette override: Wei gold, Shu green, Wu blue. Falls back to
// the base season color if a kingdom has no palette entry.
function tileColorFor(season) {
  return KingdomConfig[activeId()]?.palette?.[season] ?? SeasonConfig[season].tileColor
}

function nextSeason(current) {
  const idx = SeasonOrder.indexOf(current)
  return SeasonOrder[(idx + 1) % SeasonOrder.length]
}

export function getSeasonConfig() {
  return SeasonConfig[active().season]
}

export function getWealth() {
  return active().wealth
}

export function getClickCount() {
  return active().clickCount
}

export function spendWealth(amount) {
  const k = active()
  if (amount < 0 || k.wealth < amount) return false
  k.wealth -= amount
  emit(Events.WealthChange, { wealth: k.wealth, delta: -amount })
  persist()
  return true
}

// Take rice out of this season's harvest count (sold in the Capital). Reduces every stage's click
// count equally, so the projected harvest drops by exactly `n`. Returns how many were really taken.
export function takeRice(n) {
  const k = active()
  const have = getHarvestProjection()
  const take = Math.max(0, Math.min(Math.floor(n), have))
  if (!take) return 0
  const c = k.seasonClicks
  for (const s of ['PREP', 'PLANT', 'MAINTAIN', 'HARVEST']) c[s] = Math.max(0, (c[s] ?? 0) - take)
  persist()
  return take
}

// CO-OP: while a guest is in the player's kingdom, what the player EARNS goes into the shared coffer instead of the treasury (game/coopManager.js
// installs a tap). The tap is given the amount and returns how much of it it took; the rest is credited as usual. Only production passes
// through it (paw clicks, the harvest, tax tribute, bounties) — never purchases, refunds, rewards or coffer payouts.
let earningTap = null
export function setEarningTap(fn) { earningTap = typeof fn === 'function' ? fn : null }
const afterTap = (amount) => (earningTap && amount > 0 ? Math.max(0, amount - Math.max(0, Math.min(amount, Number(earningTap(amount)) || 0))) : amount)

// opts.tally = true asks the HUD to count the treasury up slowly with a "+N" flourish (used for purchased Mao Coin).
export function earnWealth(amount, opts = {}) {
  if (opts.production) amount = afterTap(amount) // (opts.production: this is something the player earned by playing)
  if (amount <= 0) return
  const k = active()
  k.wealth += amount
  emit(Events.WealthChange, { wealth: k.wealth, delta: amount, tally: !!opts.tally })
  persist()
}

// Streak-milestone payouts arrive from the server via accountManager. Credit
// the active kingdom's wealth so the player sees the gold land on day-N login.
on(Events.StreakRewardClaimed, ({ amount }) => {
  if (typeof amount === 'number' && amount > 0) earnWealth(amount)
})

// Lifetime tallies used by the Registry. Bounty is also credited to the
// player's wealth on the spot — the counter here just tracks the cumulative
// total separately so the Registry can show "earned X from bounties".
export function recordGoonSlain(bounty) {
  const k = active()
  k.goonsSlain = (k.goonsSlain ?? 0) + 1
  if (bounty > 0) {
    k.bountyTotal = (k.bountyTotal ?? 0) + bounty
    const credit = afterTap(bounty)
    k.wealth += credit
    emit(Events.WealthChange, { wealth: k.wealth, delta: credit })
  }
  persist()
}

export function getGoonsSlain() {
  return active().goonsSlain ?? 0
}

export function getBountyTotal() {
  return active().bountyTotal ?? 0
}

// Fine the active kingdom — caps at current wealth so the player can't go
// negative. Unlike spendWealth, this always "succeeds" (taking whatever's
// there); the AFK Emperor doesn't care if you're broke.
export function fineWealth(amount) {
  if (amount <= 0) return 0
  const k = active()
  const taken = Math.min(amount, k.wealth)
  if (taken <= 0) return 0
  k.wealth -= taken
  emit(Events.WealthChange, { wealth: k.wealth, delta: -taken })
  persist()
  return taken
}

export function getGoonsOwned(goonId) {
  return active().goons?.[goonId] ?? 0
}

export function incrementGoonsOwned(goonId) {
  const k = active()
  if (!k.goons) k.goons = {}
  k.goons[goonId] = (k.goons[goonId] ?? 0) + 1
  emit(Events.GoonOwnedChange, { goonId, owned: k.goons[goonId] })
  persist()
  return k.goons[goonId]
}

// Work Mode — where the citizens are deployed (FIELDS = farming, VILLAGE =
// defending). Drives income (taxManager) + defense (wildGoons) + worker AI.
export function getWorkMode() {
  return active().workMode ?? WorkModes.FIELDS
}
export function setWorkMode(mode) {
  if (mode !== WorkModes.FIELDS && mode !== WorkModes.VILLAGE) return
  const k = active()
  if (k.workMode === mode) return
  k.workMode = mode
  persist()
  emit(Events.WorkModeChange, { mode })
}
export function toggleWorkMode() {
  setWorkMode(getWorkMode() === WorkModes.FIELDS ? WorkModes.VILLAGE : WorkModes.FIELDS)
}

// Player-set tax rate (CR-4): how hard you tax your citizens. Higher = more
// tribute now, lower Honor; lower = less income, higher Honor. Per-kingdom.
export function getTaxRate() {
  return active().taxRate ?? TaxConfig.defaultRate
}
export function setTaxRate(rate) {
  const r = Math.max(TaxConfig.minRate, Math.min(TaxConfig.maxRate, Number(rate) || 0))
  const k = active()
  if (k.taxRate === r) return
  k.taxRate = r
  persist()
  emit(Events.TaxRateChange, { rate: r })
}

// Injured citizens (hurt fighting goons) — they collect less until healed.
export function getInjuredCount() {
  return Math.min(active().injuredCount ?? 0, totalCitizens())
}
export function injureCitizen() {
  const k = active()
  const cur = k.injuredCount ?? 0
  if (cur >= totalCitizens()) return false
  k.injuredCount = cur + 1
  persist()
  emit(Events.CitizenInjured, { count: k.injuredCount })
  return true
}
export function healCitizen() {
  const k = active()
  if (!k.injuredCount) return false
  k.injuredCount = Math.max(0, k.injuredCount - 1)
  persist()
  emit(Events.CitizenHealed, { count: k.injuredCount })
  return true
}
export function healAllInjuries() {
  const k = active()
  if (!k.injuredCount) return
  k.injuredCount = 0
  persist()
  emit(Events.CitizenHealed, { count: 0 })
}

// Total citizens owned across all goon types — the cap on how many can be
// educated (one graduation per citizen).
export function totalCitizens() {
  return Object.values(GoonTypes).reduce((s, id) => s + getGoonsOwned(id), 0)
}

export function getEducatedCount() {
  return Math.min(active().educatedCount ?? 0, totalCitizens())
}

// A citizen graduated from a School → mark one citizen educated in the roster
// (which pays the one-time CitizenEducationPayout) and bump the legacy counter.
// Returns true if it actually counted.
export function educateCitizen() {
  const k = active()
  const cur = k.educatedCount ?? 0
  if (cur >= totalCitizens()) return false
  const grad = citizens.educateOne?.() ?? { ok: false }
  k.educatedCount = cur + 1
  persist()
  emit(Events.CitizenEducated, { count: k.educatedCount, payout: grad.payout ?? 0, type: grad.type ?? null })
  return true
}

// Marriage and education tweak goon counts and per-type levels; both
// belong to the active kingdom's slice and persist atomically.
// `reason` is why a citizen left (starved, turned feral, shot…): every loss is written to profile.citizenLoss (the last 30), so what happened
// to a player's cats can always be read from their save instead of guessed.
export function adjustGoonsOwned(goonId, delta, reason = null) {
  const k = active()
  if (!k.goons) k.goons = {}
  const next = Math.max(0, (k.goons[goonId] ?? 0) + delta)
  if (delta < 0) {
    const p = getProfile()
    p.citizenLoss = [...(p.citizenLoss ?? []), { at: Date.now(), goonId, n: -delta, reason: reason ?? 'unknown', left: next }].slice(-30)
    const before = gravesTotal(k.graves)
    k.graves = gravesAfter(k.graves, reason ?? 'unknown', -delta) // a headstone in the cemetery, and the Registry's "lost" count
    if (gravesTotal(k.graves) !== before) emit(Events.GravesChange, { graves: k.graves })
  }
  k.goons[goonId] = next
  emit(Events.GoonOwnedChange, { goonId, owned: next })
  persist()
  return next
}

// Citizens in the Hospital (hospital.js) — still owned, but out of the field: no clicks, no wages.
export function getHospitalizedCount(goonId = null) { return hospital.count(goonId) }
// The kingdom's graves: { starved, feral, hospital, shot, other, total } (every citizen lost; see hungerRules.gravesAfter).
export function getGraves() { return { ...(active().graves ?? {}) } }
export function getGravesTotal() { return gravesTotal(active().graves) }

export function getEducationLevel(goonId) {
  return active().educationLevels?.[goonId] ?? 0
}

export function incrementEducationLevel(goonId) {
  const k = active()
  if (!k.educationLevels) k.educationLevels = {}
  k.educationLevels[goonId] = (k.educationLevels[goonId] ?? 0) + 1
  emit(Events.EducationLevelChange, { goonId, level: k.educationLevels[goonId] })
  persist()
  return k.educationLevels[goonId]
}

export function getLastTaxTick() {
  return active().lastTaxTick ?? 0
}

export function setLastTaxTick(t) {
  active().lastTaxTick = t
}

function sourceColorFor(season, cyclesCompleted) {
  if (cyclesCompleted === 0 && season === Seasons.PREP) return tileColorFor(Seasons.PREP)
  const idx = SeasonOrder.indexOf(season)
  const prev = SeasonOrder[(idx - 1 + SeasonOrder.length) % SeasonOrder.length]
  return tileColorFor(prev)
}

// Broadcast the active kingdom's state. Called by main on boot AND by
// kingdomManager.switchTo() after switching — same flow, no special cases.
export function init() {
  const k = active()
  emit(Events.SeasonChange, {
    season: k.season,
    config: { ...SeasonConfig[k.season], tileColor: tileColorFor(k.season) },
    progress: k.seasonProgress,
    sourceColor: sourceColorFor(k.season, k.cyclesCompleted),
  })
  emit(Events.WealthChange, { wealth: k.wealth, delta: 0 })
  emit(Events.ClickCountChange, { clickCount: k.clickCount })
}

// The Mao's current per-click coin value — the money-maker. PURE formula (CR-2):
// base 5 (+1 per PhD) + ClickEconomy.perPeasant per peasant + perScribe per scribe (1 / 2 since V1.18), doubled while citizens are
// sent to the FIELDS (village left undefended). Injured citizens — and citizens resting indoors — contribute 0.
export function getClickValue() {
  const P = getGoonsOwned(GoonTypes.PEASANT_CAT) - getHospitalizedCount(GoonTypes.PEASANT_CAT) // (patients in the Hospital add nothing)
  const S = getGoonsOwned(GoonTypes.SCRIBE_CAT) - getHospitalizedCount(GoonTypes.SCRIBE_CAT)
  const injured = getInjuredCount()
  const working = citizensWorking() // citizens asleep (REST) add nothing to a click
  const effP = working ? Math.max(0, P - injured) : 0                  // injuries hit peasants first…
  const effS = working ? Math.max(0, S - Math.max(0, injured - P)) : 0 // …then scribes
  return computeClickValue({ peasants: effP, scribes: effS, phd: phdCount(), fields: getWorkMode() === WorkModes.FIELDS })
}

// PhDs permanently raise the click base. CR-6: counts subjects mastered to the
// PhD tier in the player's self-education record (profileManager).
function phdCount() {
  return profile.phdSubjectCount?.() ?? 0
}

function ensureClock(k) {
  if (!k.seasonClicks) k.seasonClicks = { PREP: 0, PLANT: 0, MAINTAIN: 0, HARVEST: 0 }
  if (!k.seasonStartedAt) k.seasonStartedAt = Date.now()
}

// A PLAYER click (the paw): pays clickValue immediately AND adds to THIS season's
// counter (the discipline ladder). Goons no longer fake-click for income —
// passive tribute is taxManager's job, so idle citizens can't fill the harvest.
// The rhythm combo (game/combo.js): a session thing, never saved. getCombo() is what the HUD meter and the sounds read.
let combo = emptyCombo()
export function getCombo(now = performance.now()) {
  const value = comboAt(combo, now, ComboConfig)
  return { value, mult: comboMult(value, ComboConfig), live: comboLive(combo, now, ComboConfig) }
}
export function resetCombo() { combo = emptyCombo() }

export function registerClick() {
  const k = active()
  ensureClock(k)
  const now = performance.now(), before = comboAt(combo, now, ComboConfig)
  combo = comboAfterClick(combo, now, ComboConfig)
  glint.onPawClick(Date.now()) // (a glint up on the field? this click catches it — and is the first boosted one)
  const hotMult = glint.takeMult()
  const mult = comboMult(combo.value, ComboConfig) * hotMult
  const delta = Math.round(getClickValue() * mult) // (the combo — and a caught glint — multiply the click: the scribe shows the real number)
  const credit = afterTap(delta)
  k.clickCount += 1
  k.wealth += credit
  k.seasonClicks[k.season] = (k.seasonClicks[k.season] ?? 0) + 1
  emit(Events.Click, { season: k.season, delta, clicks: k.seasonClicks[k.season], combo: combo.value, mult, hot: hotMult > 1, boostLeft: glint.boostLeft() })
  emit(Events.ComboChange, { value: combo.value, mult: comboMult(combo.value, ComboConfig), live: true, max: combo.value >= 1 && before < 1, broke: false, hot: hotMult > 1 ? glint.boostLeft() + 1 : 0 })
  emit(Events.WealthChange, { wealth: k.wealth, delta: credit })
  emit(Events.ClickCountChange, { clickCount: k.clickCount })
  persist()
}

// A DIRECTED citizen's kneel (scene/peasants.js directCitizen): a notch for this season and payMult of a click's value.
export function registerTend({ goonId, payMult = 0.5 }) {
  const k = active()
  ensureClock(k)
  const delta = Math.round(getClickValue() * payMult)
  const credit = afterTap(delta)
  k.wealth += credit
  k.seasonClicks[k.season] = (k.seasonClicks[k.season] ?? 0) + 1
  emit(Events.CitizenTended, { goonId, delta, clicks: k.seasonClicks[k.season] })
  emit(Events.WealthChange, { wealth: k.wealth, delta: credit })
  persist()
  return delta
}
// Clicks LOST this season (the crow ate a notch): the rows shrink back and the harvest is poorer. Returns how many were lost.
export function loseClicks(n, why = '') {
  const k = active()
  ensureClock(k)
  const before = k.seasonClicks[k.season] ?? 0, after = Math.max(0, before - n)
  k.seasonClicks[k.season] = after
  emit(Events.ClicksLost, { lost: before - after, clicks: after, why })
  persist()
  return before - after
}

// Time-boxed seasons — driven from the main loop. Each season runs for
// SeasonDurationsMs[season]; when it elapses we advance. Throttled progress
// events feed the HUD season bar.
let _lastProgressEmit = 0
export function tickSeason() {
  const k = active()
  ensureClock(k)
  const dur = SeasonDurationsMs[k.season] ?? 20000
  const now = Date.now()
  const elapsed = now - k.seasonStartedAt
  if (now - _lastProgressEmit > 150) {
    _lastProgressEmit = now
    emit(Events.SeasonProgress, { season: k.season, elapsed, duration: dur, clicks: k.seasonClicks?.[k.season] ?? 0 })
  }
  if (elapsed >= dur) advance()
}

function advance() {
  const k = active()
  const prev = k.season
  // Leaving HARVEST closes the cycle: harvest = the season bottleneck × riceValue.
  if (prev === Seasons.HARVEST) {
    const c = k.seasonClicks ?? {}
    const harvestable = Math.max(0, Math.min(c.PREP ?? 0, c.PLANT ?? 0, c.MAINTAIN ?? 0, c.HARVEST ?? 0))
    const bonus = harvestable * RiceValue
    if (bonus > 0) {
      const credit = afterTap(bonus)
      k.wealth += credit
      emit(Events.WealthChange, { wealth: k.wealth, delta: credit })
    }
    emit(Events.Harvest, { harvestable, bonus })
    k.seasonClicks = { PREP: 0, PLANT: 0, MAINTAIN: 0, HARVEST: 0 }
    k.cyclesCompleted = (k.cyclesCompleted ?? 0) + 1
  }
  k.season = nextSeason(prev)
  k.seasonStartedAt = Date.now()
  k.seasonProgress = 0
  emit(Events.SeasonChange, {
    season: k.season,
    previous: prev,
    config: { ...SeasonConfig[k.season], tileColor: tileColorFor(k.season) },
    cyclesCompleted: k.cyclesCompleted,
    sourceColor: sourceColorFor(k.season, k.cyclesCompleted),
  })
  persist()
}

// Live per-season click counts + the projected harvest (the min bottleneck), for the HUD.
export function getSeasonClicks() {
  return active().seasonClicks ?? { PREP: 0, PLANT: 0, MAINTAIN: 0, HARVEST: 0 }
}
export function getHarvestProjection() {
  const c = getSeasonClicks()
  return Math.max(0, Math.min(c.PREP ?? 0, c.PLANT ?? 0, c.MAINTAIN ?? 0, c.HARVEST ?? 0))
}
